package org.example.project.ui

import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import org.jetbrains.compose.resources.DrawableResource
import org.jetbrains.compose.resources.painterResource
import kotlinproject.composeapp.generated.resources.*

private val DIFFICULTY_KEYS = listOf("easy", "medium", "hard")

private val avatarDrawables: List<DrawableResource> = listOf(
    Res.drawable.avatargreen,
    Res.drawable.elephant,
    Res.drawable.giraffe,
    Res.drawable.koala,
    Res.drawable.leo,
    Res.drawable.loki
)
private val avatarNames: List<String> = listOf(
    "avatargreen", "elephant", "giraffe", "koala", "leo", "loki"
)

@Composable
fun SetupScreen(
    initialLangCode: String, // NEU: Empfängt die Sprache
    onStartGame: (
        name: String,
        avatarIndex: Int,
        avatarName: String,
        langCode: String,
        difficulty: String,
        mode: Int
    ) -> Unit
) {
    var name by remember { mutableStateOf("Gast") }
    var selectedAvatar by remember { mutableStateOf(0) }

    // NEU: Sucht die richtige Sprache aus der Liste, basierend auf der Login-Auswahl
    val initialLanguage = remember(initialLangCode) {
        appLanguages.find { it.code == initialLangCode } ?: appLanguages[0]
    }
    var selectedLang by remember { mutableStateOf(initialLanguage) }

    var expanded by remember { mutableStateOf(false) }
    var difficultyIndex by remember { mutableStateOf(0) }
    var mode by remember { mutableStateOf(0) }

    val blue = Color(0xFF113E99)
    val box = Color(0xFF3B68B8)
    val green = Color(0xFF4C7B5B)

    Column(modifier = Modifier.fillMaxSize().background(blue).padding(24.dp), horizontalAlignment = Alignment.CenterHorizontally) {

        // FIX: Richtiges Logo in Farbe statt Platzhalter-Icon
        Image(
            painter = painterResource(Res.drawable.as_logo_transparent),
            contentDescription = "Logo",
            modifier = Modifier.size(80.dp)
        )
        Spacer(modifier = Modifier.height(8.dp))

        Text(L10n.t(L10n.welcome, selectedLang.code), color = Color.White, fontSize = 24.sp, fontWeight = FontWeight.Bold)

        // Avatar Auswahl
        Row(modifier = Modifier.padding(vertical = 20.dp).horizontalScroll(rememberScrollState())) {
            avatarDrawables.forEachIndexed { i, res ->
                Box(modifier = Modifier.size(65.dp).padding(4.dp).clip(CircleShape)
                    .background(if (selectedAvatar == i) Color.White.copy(0.2f) else Color.Transparent)
                    .border(if (selectedAvatar == i) 2.dp else 0.dp, Color.Yellow, CircleShape)
                    .clickable { selectedAvatar = i }, contentAlignment = Alignment.Center) {
                    Image(painter = painterResource(res), contentDescription = null, modifier = Modifier.size(45.dp).clip(CircleShape))
                }
            }
        }

        SetupField(name) { name = it }
        Spacer(modifier = Modifier.height(12.dp))

        // Sprach-Dropdown
        Box(modifier = Modifier.fillMaxWidth().height(50.dp).background(box, RoundedCornerShape(12.dp)).clickable { expanded = true }.padding(horizontal = 16.dp), contentAlignment = Alignment.CenterStart) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Image(painter = painterResource(selectedLang.flagRes), contentDescription = null, modifier = Modifier.size(24.dp))
                Spacer(Modifier.width(12.dp))
                Text(selectedLang.name, color = Color.White)
            }
            DropdownMenu(expanded = expanded, onDismissRequest = { expanded = false }, modifier = Modifier.background(box)) {
                appLanguages.forEach { lang ->
                    DropdownMenuItem(
                        text = {
                            Row(verticalAlignment = Alignment.CenterVertically) {
                                Image(painter = painterResource(lang.flagRes), contentDescription = null, modifier = Modifier.size(20.dp))
                                Spacer(Modifier.width(12.dp))
                                Text(lang.name, color = Color.White)
                            }
                        },
                        onClick = { selectedLang = lang; expanded = false }
                    )
                }
            }
        }

        // Schwierigkeit
        Text(L10n.t(L10n.difficultyLabel, selectedLang.code), color = Color.White, modifier = Modifier.padding(top = 16.dp))
        Row(modifier = Modifier.fillMaxWidth().padding(top = 8.dp)) {
            listOf(L10n.easy, L10n.medium, L10n.hard).forEachIndexed { i, d ->
                Box(modifier = Modifier.weight(1f).height(40.dp).padding(4.dp)
                    .background(if (difficultyIndex == i) green else box, RoundedCornerShape(8.dp))
                    .clickable { difficultyIndex = i }, contentAlignment = Alignment.Center) {
                    Text(L10n.t(d, selectedLang.code), color = if (difficultyIndex == i) Color.Yellow else Color.White, fontSize = 12.sp)
                }
            }
        }

        // Modus
        val modes = listOf(L10n.modeSingle, L10n.modeCreate, L10n.modeJoin)
        modes.forEachIndexed { i, m ->
            Row(modifier = Modifier.fillMaxWidth().padding(top = 8.dp).height(45.dp).background(if (mode == i) green else box, RoundedCornerShape(8.dp)).clickable { mode = i }.padding(horizontal = 12.dp), verticalAlignment = Alignment.CenterVertically) {
                Box(modifier = Modifier.size(16.dp).border(2.dp, Color.White, CircleShape).background(if (mode == i) Color.Yellow else Color.Transparent, CircleShape))
                Text(L10n.t(m, selectedLang.code), color = Color.White, modifier = Modifier.padding(start = 8.dp))
            }
        }

        Spacer(modifier = Modifier.weight(1f))
        Button(
            onClick = {
                onStartGame(
                    name,
                    selectedAvatar,
                    avatarNames[selectedAvatar],
                    selectedLang.code,
                    DIFFICULTY_KEYS[difficultyIndex],
                    mode
                )
            },
            modifier = Modifier.fillMaxWidth().height(50.dp),
            colors = ButtonDefaults.buttonColors(containerColor = green)
        ) {
            Text(L10n.t(L10n.start, selectedLang.code), color = Color.White, fontWeight = FontWeight.Bold)
        }
    }
}

@Composable
private fun SetupField(v: String, onV: (String) -> Unit) {
    BasicTextField(value = v, onValueChange = onV, textStyle = TextStyle(Color.White), decorationBox = { inner ->
        Box(modifier = Modifier.fillMaxWidth().height(50.dp).background(Color(0xFF3B68B8), RoundedCornerShape(12.dp)).padding(horizontal = 16.dp), contentAlignment = Alignment.CenterStart) { inner() }
    })
}